import { useEffect, useMemo, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar.jsx";
import { Button } from "../../components/ui/button.jsx";
import { Input } from "../../components/ui/input.jsx";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog.jsx";
import DialerPanel from "../../components/voice/DialerPanel.jsx";
import { Phone, Search } from "lucide-react";

const normalize = (value) => String(value || "").toLowerCase();

export default function VoiceDialerDialog({
  open,
  onOpenChange,
  contacts = [],
  onDial,
}) {
  const [query, setQuery] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [manualNumber, setManualNumber] = useState("");
  const [callerLabel, setCallerLabel] = useState("Neoin Voice");

  const filteredContacts = useMemo(() => {
    if (!query.trim()) return contacts;
    const needle = normalize(query);
    return contacts.filter((contact) => {
      return [contact.name, contact.email, contact.phone]
        .filter(Boolean)
        .some((value) => normalize(value).includes(needle));
    });
  }, [contacts, query]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setSelectedContact(null);
      setManualNumber("");
      setCallerLabel("Neoin Voice");
    }
  }, [open]);

  const handleSelectContact = (contact) => {
    setSelectedContact(contact);
    if (contact.phone) {
      setManualNumber(contact.phone);
    }
    if (contact.name) {
      setCallerLabel(contact.name);
    }
  };

  const handleDial = (payload) => {
    onDial?.(payload, selectedContact);
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-semibold">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-primary/10 text-primary">
              <Phone className="h-4 w-4" />
            </span>
            Bridge a new call
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-6 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
          <div className="rounded-2xl border border-border bg-muted/30 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Search className="h-3.5 w-3.5" /> Neoin directory
            </div>
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by name, email, or phone"
              className="mt-3"
            />
            <div className="mt-3 max-h-64 overflow-auto rounded-2xl border border-border bg-background/40">
              {filteredContacts.length ? (
                <ul className="divide-y divide-border/60 text-sm">
                  {filteredContacts.map((contact) => {
                    const key = contact.id || contact.email || contact.phone || contact.name;
                    const isActive = selectedContact && (selectedContact.id || selectedContact.email || selectedContact.phone) === (contact.id || contact.email || contact.phone);
                    return (
                      <li key={key}>
                        <button
                          type="button"
                          onClick={() => handleSelectContact(contact)}
                          className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition ${isActive ? "bg-primary/10 text-foreground" : "hover:bg-muted/40"}`}
                        >
                          <span className="flex items-center gap-3">
                            <Avatar className="h-9 w-9">
                              <AvatarImage src={`https://i.pravatar.cc/80?u=${encodeURIComponent(contact.email || contact.name || key)}`} alt={contact.name || "Contact"} referrerPolicy="no-referrer" />
                              <AvatarFallback>{(contact.name || "C").slice(0, 2).toUpperCase()}</AvatarFallback>
                            </Avatar>
                            <span>
                              <span className="block font-semibold text-foreground">{contact.name || contact.email || contact.phone}</span>
                              <span className="text-xs text-muted-foreground">
                                {[contact.email, contact.phone].filter(Boolean).join(" • ") || "Mobile directory"}
                              </span>
                            </span>
                          </span>
                          <span className="text-[11px] uppercase tracking-wide text-primary">Select</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="p-6 text-center text-xs text-muted-foreground">No contacts match this search.</div>
              )}
            </div>
            {selectedContact && selectedContact.phone && (
              <div className="mt-3 rounded-2xl border border-primary/40 bg-primary/5 px-4 py-2 text-xs text-primary">
                Selected: {selectedContact.name || selectedContact.phone} • {selectedContact.phone}
              </div>
            )}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="mt-4 w-full"
              onClick={() => {
                setSelectedContact(null);
                setManualNumber("");
                setCallerLabel("Neoin Voice");
              }}
            >
              Clear selection
            </Button>
          </div>
          <DialerPanel
            contacts={contacts}
            initialNumber={manualNumber}
            initialCallerId={callerLabel}
            onNumberChange={setManualNumber}
            onCallerIdChange={setCallerLabel}
            onDial={handleDial}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
